<template>
    <v-form
        ref="form"
        v-bind="formProps"
        style="max-height: 100%"
        @update:model-value="onValidity"
        @submit.prevent="emit('submit')"
    >
        <v-card v-bind="$attrs" class="dialog-card">
            <div class="dialog-card__head">
                <h3 class="dialog-card__title">
                    <slot name="title">{{ title }}</slot>
                    <span
                        v-if="context || $slots['context']"
                        class="dialog-card__context text-data"
                    >
                        · <slot name="context">{{ context }}</slot>
                    </span>
                </h3>
                <v-spacer />
                <slot name="header-actions" />
                <v-btn
                    variant="text"
                    color="secondary"
                    size="small"
                    density="comfortable"
                    icon="mdi-close"
                    aria-label="Close"
                    @click="emit('close')"
                />
            </div>
            <div class="dialog-card__body">
                <slot :valid="valid" />
            </div>
            <div v-if="$slots['actions']" class="dialog-card__actions">
                <slot name="actions" :valid="valid" />
            </div>
        </v-card>
    </v-form>
</template>
<script setup lang="ts">
// Standard dialog shell: an uppercase header strip with an optional mono
// context (the object being acted on - ticket key, version, user), a body,
// and a footer action row. Extends the SectionCard header language to
// dialogs. Like FormCard it wraps a v-form so submit/validation work the
// same way; unlike FormCard it is only for dialog content.
//
// Footer conventions (see the dialog treatment mockup):
// - Cancel/Close: `variant="text" color="secondary"`
// - The one confirm: `variant="flat"` with a semantic color - `primary` for
//   affirmative, `error` for destructive, `release` for shipping a version.
// - A destructive secondary action (e.g. Delete on an edit dialog) sits
//   far-left: `variant="text" color="error"` followed by a <v-spacer>.
//
// Width comes from the dialog, not this card: put `class="dialog-sm|md|lg"`
// on the wrapping <v-dialog>.
import type { VForm } from "vuetify/components";
import { useFormValidity } from "@/composables/formValidity";

defineOptions({
    inheritAttrs: false,
});

type Props = {
    title?: string | undefined;
    /** Mono identifier rendered after the title, e.g. a ticket key. */
    context?: string | undefined;
    formProps?: VForm["$props"] | undefined;
};

withDefaults(defineProps<Props>(), {
    title: "",
    context: "",
    formProps: () => ({}),
});

const emit = defineEmits<{
    submit: [];
    close: [];
}>();

const { valid, onValidity, validate, reset, resetValidation } = useFormValidity();

defineExpose({ reset, resetValidation, validate });
</script>
<style lang="scss">
// Pin the header/footer and scroll only the body when a dialog outgrows the
// viewport. Vuetify's `scrollable` prop only knows how to constrain
// v-card-text, which this card doesn't use, so the flex chain from the
// overlay content down to the body is established here instead.
.v-dialog > .v-overlay__content:has(> form > .dialog-card) {
    display: flex;
    flex-direction: column;

    > form {
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
}

.dialog-card {
    display: flex;
    flex-direction: column;
    min-height: 0;

    &__head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        min-height: 44px;
        padding: 0.375rem 0.5rem 0.375rem 1rem;
        border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    }

    &__title {
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: rgba(var(--v-theme-on-surface), 0.7);
    }

    &__context {
        font-size: 0.78125rem;
        font-weight: 500;
        letter-spacing: normal;
        text-transform: none;
        color: rgba(var(--v-theme-on-surface), 0.55);
    }

    &__body {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 1rem;

        // The markdown editor's global 30vh minimum is far too tall inside a
        // dialog; give it a fixed, internally-scrolling height instead.
        .md-editor {
            height: 280px;
            min-height: 200px;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.25rem;
        padding: 0.5rem 0.75rem;
        border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
}
</style>
